import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useQuery } from '@tanstack/react-query';

import { BillingInterval } from 'app/typings/billingIntervals';
import { ChannelManager } from 'app/typings/channelManagers';
import type { ExtranetHotelInfoResponse } from 'app/typings/core/extranet/hotel/hotelInfo';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import { apiRequest } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

export const mapExtranetHotelInfoToHotel = (
  response: ExtranetHotelInfoResponse
): Hotel => ({
  id: response.id,
  groupId: 0,
  name: response.name,
  stars: response.stars ?? 0,
  address: {
    street: '',
    zipCode: '',
    city: '',
    department: '',
    region: '',
    countryId: response.countryId,
    full: '',
  },
  location: {
    locality: '',
    coords: { lat: 0, lng: 0 },
  },
  phone: '',
  notifEmails: [],
  roomCount: response.roomCount,
  services: { safety: [], services: [] },
  brief: {
    bookingUrl: '',
    bookingComUrl: '',
    tagIds: [],
  },
  pspOnboardingDone: false,
  payoutEnabled: false,
  paymentReady: false,
  channelManager:
    (response.channelManager as ChannelManager) || ChannelManager.NONE,
  rateMode: response.rateMode as Hotel['rateMode'],
  billingEmails: [],
  countryId: response.countryId,
  billingInterval: BillingInterval.MONTHLY,
  autoDiscountRate: response.autoDiscountRate,
  lastMinuteAutoDiscountDays: response.lastMinuteAutoDiscountDays,
  defaultNightRoomDiscount: response.defaultNightRoomDiscount,
  defaultNightRoomDiscountConfig: response.defaultNightRoomDiscountConfig,
  lastMinuteNightRoomDiscountRate: response.lastMinuteNightRoomDiscountRate,
  lastMinuteNightRoomDiscountDays: response.lastMinuteNightRoomDiscountDays,
  supplierId: response.supplierId,
  stockScrapperBarModifierValue: response.stockScrapperBarModifierValue,
  stockScrapperBarModifierType:
    response.stockScrapperBarModifierType as Hotel['stockScrapperBarModifierType'],
  stockScrapperEnabled: response.stockScrapperEnabled,
  issuingActivated: response.issuingActivated,
  hasVccBookings: false,
  isLegacy: response.isLegacy,
  slug: response.slug,
  connectedBarBb: false,
});

export function useGetExtranetHotelInfo(hotelId?: number) {
  return useQuery({
    queryKey: [CORE_QUERY_KEYS.hotelInfo, { id: hotelId }],
    queryFn: hotelId
      ? () =>
          apiRequest({
            path: ENDPOINTS.getHotelInfo,
            params: { id: hotelId.toString() },
          }).then((response: ExtranetHotelInfoResponse) =>
            mapExtranetHotelInfoToHotel(response)
          )
      : undefined,
    enabled: !!hotelId,
    refetchOnWindowFocus: false,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
  });
}
